<div class="container-fluid complyon-page obligations-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell obligations-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title obligations-title mb-2 text-l">Obligations register</h1>
      <p class="complyon-subtitle obligations-subtitle mb-0 text-s">
        Everything the organisation is required to do - from laws, regulations, contracts and board resolutions - mapped to owners, controls and the evidence that proves compliance.
      </p>
    </section>

    <section class="row g-3 mb-3">
      <div
        v-for="card in summary_cards"
        :key="card.label"
        class="col-12 col-md-6 col-xl-3"
      >
        <article class="complyon-card obligations-card rounded-4 p-3 h-100">
          <div class="obligations-stat-label text-xs">{{ card.label }}</div>
          <div class="obligations-stat-value" :class="`is-${card.tone}`">{{ card.value }}</div>
          <div class="obligations-stat-note text-xs">{{ card.note }}</div>
        </article>
      </div>
    </section>

    <section class="complyon-panel obligations-panel rounded-4 overflow-hidden">
      <div class="d-flex flex-column flex-xl-row align-items-xl-start justify-content-between gap-3 px-3 px-md-4 pt-3 pt-md-4 pb-3">
        <div>
          <div class="obligations-section-title text-s">Obligations register</div>
          <div class="obligations-section-subtitle text-xs">
            Everything the organisation is required to do - from laws, regulations, contracts, policies and board resolutions - mapped to an owner, a frequency and the controls and evidence that prove compliance.
          </div>
        </div>
        <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2">
          <i class="bi bi-plus"></i>
          <span>New obligation</span>
        </button>
      </div>

      <div class="d-flex flex-column flex-xl-row align-items-xl-center justify-content-between gap-3 px-3 px-md-4 pb-3">
        <div class="d-inline-flex flex-wrap align-items-center gap-1 obligations-filter-bar rounded-4 p-1">
          <button
            v-for="tab in category_tabs"
            :key="tab.id"
            type="button"
            class="btn obligations-filter-btn"
            :class="active_filter === tab.id ? 'active' : ''"
            @click="set_filter(tab.id)"
          >
            <span>{{ tab.label }}</span>
            <span class="obligations-filter-count">{{ tab.count }}</span>
          </button>
        </div>

        <div class="obligations-search-wrap position-relative">
          <i class="bi bi-search obligations-search-icon"></i>
          <input
            v-model="search_query"
            type="search"
            class="form-control obligations-search-input"
            placeholder="Search..."
            aria-label="Search obligations"
          >
        </div>
      </div>

      <div class="table-responsive">
        <table class="table complyon-table obligations-table align-middle mb-0">
          <thead>
            <tr>
              <th class="ps-3 text-xs">Ref</th>
              <th class="text-xs">Obligation</th>
              <th class="text-xs">Source</th>
              <th class="text-xs">Owner</th>
              <th class="text-xs">Frequency</th>
              <th class="text-xs">Status</th>
              <th class="text-xs"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in filtered_obligations"
              :key="row.ref"
              :class="{ 'obligations-row-highlight': row.highlight }"
              class="obligations-row-clickable"
              role="button"
              tabindex="0"
              :title="`Open ${row.obligation}`"
              @click="open_obligation_drawer(row)"
              @keydown.enter.prevent="open_obligation_drawer(row)"
              @keydown.space.prevent="open_obligation_drawer(row)"
            >
              <td class="ps-3 fw-semibold text-nowrap">{{ row.ref }}</td>
              <td class="fw-semibold">{{ row.obligation }}</td>
              <td>{{ row.source }}</td>
              <td>{{ row.owner }}</td>
              <td>{{ row.frequency }}</td>
              <td>
                <span class="badge rounded-pill obligations-pill text-xs" :class="row.status_class">
                  {{ row.status }}
                </span>
              </td>
              <td class="text-end pe-3">
                <i class="bi bi-chevron-right obligations-row-arrow"></i>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>

  <div v-if="active_obligation" class="command-centre-drawer-backdrop" @click="close_obligation_drawer"></div>

  <aside
    v-if="active_obligation"
    class="command-centre-drawer obligations-drawer"
    aria-label="Obligation drawer"
    @click.stop
  >
    <div class="command-centre-drawer-header border-bottom obligations-drawer-header">
      <div class="d-flex align-items-start justify-content-between gap-3">
        <div class="min-w-0">
          <div class="obligations-drawer-kicker text-uppercase">{{ active_obligation.section }}</div>
          <h2 class="obligations-drawer-title mb-1">{{ active_obligation.title }}</h2>
          <div class="obligations-drawer-subtitle">{{ active_obligation.subtitle }}</div>
        </div>
        <div class="d-flex align-items-center gap-2 flex-shrink-0">
          <span class="badge rounded-pill" :class="active_obligation.status_class">{{ active_obligation.status }}</span>
          <button type="button" class="btn btn-link text-decoration-none p-0 obligations-drawer-close" @click="close_obligation_drawer" aria-label="Close drawer">
            <i class="bi bi-x-lg fs-5"></i>
          </button>
        </div>
      </div>
    </div>

    <div ref="obligationDrawerBody" class="command-centre-drawer-body obligations-drawer-body">
      <div class="obligations-drawer-details-grid mb-4">
        <div v-for="item in active_obligation.details" :key="item.label" class="obligations-drawer-detail">
          <div class="obligations-drawer-detail-label text-nowrap">{{ item.label }}</div>
          <div class="obligations-drawer-detail-value">{{ item.value }}</div>
        </div>
      </div>

      <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
        <div class="obligations-drawer-section-title">{{ active_obligation.evidence_section.title }}</div>
        <span class="badge rounded-pill obligations-drawer-badge">{{ active_obligation.evidence_section.badge }}</span>
      </div>

      <div class="obligations-drawer-checks mb-4">
        <div v-for="item in active_obligation.evidence_checks" :key="item.title" class="obligations-drawer-check rounded-4 p-3">
          <div class="d-flex align-items-start gap-3">
            <span class="obligations-drawer-check-icon d-inline-flex align-items-center justify-content-center">
              <i class="bi" :class="item.icon"></i>
            </span>
            <div class="min-w-0 flex-grow-1">
              <div class="obligations-drawer-check-title">{{ item.title }}</div>
              <div class="obligations-drawer-check-subtitle">{{ item.subtitle }}</div>
            </div>
            <span class="badge rounded-pill align-self-start" :class="item.status_class">{{ item.status }}</span>
          </div>
        </div>
      </div>

      <div class="obligations-drawer-section-title mb-2">Connected records</div>
      <div class="obligations-drawer-record-grid mb-4">
        <div v-for="record in active_obligation.connected_records" :key="record.label" class="obligations-drawer-record rounded-4 p-3">
          <div class="d-flex align-items-start gap-2">
            <span class="obligations-drawer-record-icon d-inline-flex align-items-center justify-content-center">
              <i class="bi" :class="record.icon"></i>
            </span>
            <div class="min-w-0">
              <div class="obligations-drawer-record-label text-uppercase">{{ record.label }}</div>
              <div class="obligations-drawer-record-value">{{ record.value }}</div>
            </div>
            <i class="bi bi-box-arrow-up-right obligations-drawer-record-link ms-auto"></i>
          </div>
        </div>
      </div>

      <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
        <div class="obligations-drawer-section-title">Recommended action</div>
        <span class="badge rounded-pill bg-secondary-subtle text-secondary-emphasis">AI</span>
      </div>
      <div class="obligations-drawer-action rounded-4 p-3">
        {{ active_obligation.recommended_action }}
      </div>
    </div>

    <div class="command-centre-drawer-footer border-top obligations-drawer-footer">
      <div class="d-flex align-items-center justify-content-between gap-3 flex-wrap">
        <button type="button" class="btn btn-link text-decoration-none px-0 obligations-drawer-footer-link">{{ active_obligation.footer.left_label }}</button>
        <div class="d-flex align-items-center gap-2 ms-auto flex-wrap">
          <button type="button" class="btn btn-outline-secondary obligations-drawer-footer-btn">
            <i class="bi bi-folder2-open me-1"></i>
            <span>{{ active_obligation.footer.middle_label }}</span>
          </button>
          <button type="button" class="btn btn-success complyon-primary-btn governance-action-btn d-inline-flex align-items-center gap-2 obligations-drawer-footer-btn">
            <i class="bi bi-arrow-right"></i>
            <span>{{ active_obligation.footer.primary_label }}</span>
          </button>
        </div>
      </div>
    </div>
  </aside>
</div>
